import { useCallback, useMemo } from 'react';
import { switchcase, useNoticeBannersStackContext } from '@sweetgreen/garnish';

import { useLocalizationContext } from '@order/Localization';

export const useCheckoutNoticeBanners = () => {
  const { push: addNoticeBanner } = useNoticeBannersStackContext();
  const { t } = useLocalizationContext();

  const deliveryMinimumNotMet = useCallback(
    (minimumDeliveryAmount: string) => {
      addNoticeBanner({
        text: t('bag.delivery-minimum', { minimum: minimumDeliveryAmount }),
        palette: 'caution',
      });
    },
    [t, addNoticeBanner],
  );

  const productUnavailable = useCallback(
    (message: string) => {
      addNoticeBanner({
        text: message,
        palette: 'caution',
      });
    },
    [addNoticeBanner],
  );

  const restaurantIsNotAcceptingOrders = useCallback(
    (message: string) => {
      addNoticeBanner({
        text: message,
        palette: 'caution',
      });
    },
    [addNoticeBanner],
  );

  const otherKnownErrors = useCallback(
    (typename: string) => {
      addNoticeBanner({
        text: switchcase({
          InvalidBillingAccount: t('checkout.billing-account.invalid'),
          InvalidCustomerPhone: t('checkout.customer-phone.invalid'),
          InvalidTip: t('checkout.tip.invalid'),
          DeliveryAlreadyInFlight: t('checkout.delivery-in-flight'),
          DeliverySpecifyAddress: t('checkout.delivery-specify-address'),
        })(t('general.error'))(typename),
        palette: 'caution',
      });
    },
    [t, addNoticeBanner],
  );

  const unknownCheckoutError = useCallback(() => {
    addNoticeBanner({
      text: t('general.error'),
      palette: 'caution',
    });
  }, [t, addNoticeBanner]);

  return useMemo(
    () => ({
      deliveryMinimumNotMet,
      productUnavailable,
      restaurantIsNotAcceptingOrders,
      otherKnownErrors,
      unknownCheckoutError,
    }),
    [
      deliveryMinimumNotMet,
      productUnavailable,
      restaurantIsNotAcceptingOrders,
      otherKnownErrors,
      unknownCheckoutError,
    ],
  );
};
